// Tekton 共用类型定义

// PipelineRun 状态类型
export type PipelineRunStatus = 'All' | 'Succeeded' | 'Failed' | 'Running' | 'Pending' | 'Cancelled';

// 工作流类型常量
export const WORKFLOW_TYPE = {
  CD: 'CDWorkflow',
  CUSTOM: 'CustomWorkflow',
  KNATIVE: 'KnativeWorkflow',
} as const;

export type WorkflowType = typeof WORKFLOW_TYPE[keyof typeof WORKFLOW_TYPE];

// 生成短时间戳（Base36 编码，6位）
// 使用相对时间戳（从 2024-01-01 开始），进一步缩短
const generateShortTimestamp = (): string => {
  // 基准时间: 2024-01-01 00:00:00 UTC
  const baseTime = 1704067200;
  const relativeTime = Math.floor(Date.now() / 1000) - baseTime;
  // 转换为 Base36，最多 6 位可表示约 70 年
  return relativeTime.toString(36);
};

// 生成 PipelineRun 名称
// 格式: {pipelineName}-{shortTimestamp}
// 例如: deploy-definition-tcl-1abc23
// 所有元数据（cluster, component, workflow-type, triggered-by）都通过 annotations 传递
export const generatePipelineRunName = (pipelineName: string): string => {
  const shortTs = generateShortTimestamp();
  return `${pipelineName}-${shortTs}`;
};

// Pipeline 数据结构
export interface TektonPipeline {
  apiVersion: string;
  kind: string;
  metadata: {
    name: string;
    namespace: string;
    creationTimestamp: string;
    uid?: string;
    labels?: Record<string, string>;
    annotations?: Record<string, string>;
  };
  spec: {
    params?: Array<{
      name: string;
      type?: string;
      default?: string | string[];
      description?: string;
    }>;
    tasks: Array<{
      name: string;
      taskRef?: {
        name: string;
        kind?: string;
      };
    }>;
  };
}

// PipelineRun 数据结构
export interface TektonPipelineRun {
  apiVersion: string;
  kind: string;
  metadata: {
    name: string;
    namespace: string;
    creationTimestamp: string;
    labels?: Record<string, string>;
    annotations?: Record<string, string>;
  };
  spec: {
    pipelineRef?: {
      name: string;
    };
    params?: Array<{ name: string; value: string | string[] }>;
  };
  status?: {
    conditions?: Array<{
      status: string;
      type: string;
      reason?: string;
      message?: string;
      lastTransitionTime?: string;
    }>;
    startTime?: string;
    completionTime?: string;
    taskRuns?: Record<string, any>;
    childReferences?: Array<{
      name: string;
      kind: string;
      pipelineTaskName: string;
    }>;
  };
  // Results API 原始数据
  _resultsId?: string;
}

// Tekton Results API 数据结构
export interface TektonResultsResult {
  name: string;
  id: string;
  uid: string;
  created_time: string;
  updated_time: string;
  annotations?: Record<string, string>;
  summary?: {
    record: string;
    type: string;
    end_time?: string;
    status: 'SUCCESS' | 'FAILURE' | 'RUNNING' | 'PENDING' | 'CANCELLED';
  };
}

// 获取 PipelineRun 状态
export const getPipelineRunStatus = (run: TektonPipelineRun): PipelineRunStatus => {
  if (!run.status?.conditions || run.status.conditions.length === 0) return 'Pending';

  const succeededCondition = run.status.conditions.find(c => c.type === 'Succeeded');
  if (!succeededCondition) return 'Pending';

  if (succeededCondition.status === 'True') return 'Succeeded';
  if (succeededCondition.status === 'False') {
    if (succeededCondition.reason === 'Cancelled' || succeededCondition.reason === 'PipelineRunCancelled') {
      return 'Cancelled';
    }
    return 'Failed';
  }
  if (succeededCondition.status === 'Unknown') {
    const reason = succeededCondition.reason || '';
    if (reason === 'Running' || reason === 'Started') return 'Running';
    return 'Pending';
  }
  return 'Pending';
};

// 获取状态颜色
export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'Succeeded': return '#4caf50';
    case 'Failed': return '#f44336';
    case 'Running': return '#2196f3';
    case 'Pending': return '#ff9800';
    case 'Cancelled': return '#9e9e9e';
    default: return '#9e9e9e';
  }
};

// 格式化持续时间
export const formatDuration = (startTime?: string, endTime?: string, now?: number): string => {
  if (!startTime) return '-';

  const start = new Date(startTime).getTime();
  const end = endTime ? new Date(endTime).getTime() : (now || Date.now());
  const durationMs = end - start;

  const hours = Math.floor(durationMs / 3600000);
  const minutes = Math.floor((durationMs % 3600000) / 60000);
  const seconds = Math.floor((durationMs % 60000) / 1000);

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds} seconds`;
};

// 格式化时间
export const formatTime = (time?: string): string => {
  if (!time) return '-';
  return new Date(time).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// 从 apiVersion 获取 Tekton API 路径前缀
// 例如: "tekton.dev/v1" -> "tekton.dev/v1", "tekton.dev/v1beta1" -> "tekton.dev/v1beta1"
// 默认使用 v1
export const getTektonApiVersion = (apiVersion?: string): string => {
  if (apiVersion?.includes('v1beta1')) {
    return 'tekton.dev/v1beta1';
  }
  return 'tekton.dev/v1';
};

// 从 Results API 的 summary.type 中提取 API 版本
// 格式: "tekton.dev/v1.PipelineRun" 或 "tekton.dev/v1beta1.PipelineRun"
export const extractApiVersionFromType = (summaryType?: string): string => {
  if (!summaryType) return 'tekton.dev/v1';
  // 匹配 tekton.dev/v1 或 tekton.dev/v1beta1
  const match = summaryType.match(/^(tekton\.dev\/v1(?:beta1)?)/);
  return match ? match[1] : 'tekton.dev/v1';
};

// 转换 Results API 数据到 TektonPipelineRun 格式
export const convertResultToRun = (result: TektonResultsResult, namespace: string): TektonPipelineRun => {
  const pipelineRunName = result.annotations?.['object.metadata.name'] || result.name.split('/').pop() || '';
  const pipelineName = result.annotations?.['tekton.dev/pipeline'] || '';
  
  // 从 summary.type 中提取 API 版本
  const apiVersion = extractApiVersionFromType(result.summary?.type);
  
  let conditions: Array<{ status: string; type: string; reason?: string }> = [];
  const summaryStatus = result.summary?.status;
  if (summaryStatus) {
    const statusMap: Record<string, { status: string; reason: string }> = {
      'SUCCESS': { status: 'True', reason: 'Succeeded' },
      'FAILURE': { status: 'False', reason: 'Failed' },
      'RUNNING': { status: 'Unknown', reason: 'Running' },
      'PENDING': { status: 'Unknown', reason: 'Pending' },
      'CANCELLED': { status: 'False', reason: 'Cancelled' },
    };
    const mapped = statusMap[summaryStatus] || { status: 'Unknown', reason: 'Unknown' };
    conditions = [{ type: 'Succeeded', status: mapped.status, reason: mapped.reason }];
  }

  // 只有在任务完成时才设置 completionTime
  const isCompleted = summaryStatus === 'SUCCESS' || summaryStatus === 'FAILURE' || summaryStatus === 'CANCELLED';
  // 对于已完成的状态，优先使用 end_time，否则使用 updated_time 作为结束时间
  const completionTime = isCompleted ? (result.summary?.end_time || result.updated_time) : undefined;
  
  // 从 result annotations 中提取用户信息
  const annotations: Record<string, string> = {};
  if (result.annotations) {
    // backstage.io/triggered-by-name 由后端填充
    const triggeredByName = result.annotations['backstage.io/triggered-by-name'];
    if (triggeredByName) {
      annotations['backstage.io/triggered-by-name'] = triggeredByName;
    }
    const triggeredByEmail = result.annotations['backstage.io/triggered-by-email'];
    if (triggeredByEmail) {
      annotations['backstage.io/triggered-by-email'] = triggeredByEmail;
    }
    const triggeredById = result.annotations['backstage.io/triggered-by-id'];
    if (triggeredById) {
      annotations['backstage.io/triggered-by-id'] = triggeredById;
    }
    const triggeredBy = result.annotations['backstage.io/triggered-by'];
    if (triggeredBy) {
      annotations['backstage.io/triggered-by'] = triggeredBy;
    }
  }
  
  return {
    apiVersion,
    kind: 'PipelineRun',
    metadata: {
      name: pipelineRunName,
      namespace,
      creationTimestamp: result.created_time,
      ...(Object.keys(annotations).length > 0 && { annotations }),
    },
    spec: {
      pipelineRef: pipelineName ? { name: pipelineName } : undefined,
    },
    status: {
      conditions,
      startTime: result.created_time,
      completionTime,
    },
    _resultsId: result.name,
  };
};
